// src/features/MediaLibrary/MediaUpload.js - Images only version
import React, { useState, useRef, useEffect } from 'react';
import MediaService from './MediaService';
import './MediaUpload.css';

const MediaUpload = ({ onUploadComplete }) => {
  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [altText, setAltText] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Image-specific categories
  const categories = [
    'story', 'lesson', 'activity', 'reference', 'exercise', 
    'background', 'illustration', 'diagram', 'character'
  ];

  // Common image tags for educational content
  const tagSuggestions = [
    'beginner', 'intermediate', 'advanced', 'vocabulary', 'grammar',
    'culture', 'nature', 'animals', 'family', 'food', 'activities',
    'classroom', 'exercise', 'example', 'colorful', 'simple'
  ];

  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    processSelectedFile(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processSelectedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processSelectedFile = (file) => {
    // Validate that it's an image
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPG, PNG, GIF, SVG, WebP)');
      return;
    }
    
    // Validate file size (5MB limit for images)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size should be less than 5MB');
      return;
    }
    
    setSelectedFile(file);
    setUploadError('');
    setUploadSuccess(false);
    
    // Auto-fill alt text with filename without extension
    const baseName = file.name.split('.').slice(0, -1).join('.');
    setAltText(baseName.replace(/[_-]/g, ' '));
    
    // Auto-suggest category
    if (!category) {
      setCategory('illustration');
    }
    
    // Generate preview
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
  };

  const handleTagInput = (value) => {
    setTags(value);
  };

  const addSuggestedTag = (tag) => {
    const currentTags = tags.split(',').map(t => t.trim()).filter(t => t !== '');
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag].join(', ');
      setTags(newTags);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select an image');
      return;
    }

    if (!category) {
      setUploadError('Please select a category');
      return;
    }

    if (!altText.trim()) {
      setUploadError('Please provide alt text for accessibility');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);
    
    // Progress simulation
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const increment = Math.floor(Math.random() * 10) + 1;
        return prevProgress + increment > 90 ? 90 : prevProgress + increment;
      });
    }, 300);

    try {
      // Prepare tags array
      const tagsArray = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== '');
      
      // Get image dimensions
      const dimensions = await getImageDimensions(selectedFile);
      
      // Upload using MediaService - removed unused metadata parameter
      const { downloadURL, storagePath } = await MediaService.uploadMediaFile(
        selectedFile, 
        category
      );
      
      // Create asset metadata
      const assetData = {
        url: downloadURL,
        storagePath: storagePath,
        filename: selectedFile.name,
        category: category,
        tags: tagsArray,
        altText: altText,
        description: description,
        fileSize: selectedFile.size,
        dimensions: dimensions,
        type: 'image'
      };
      
      // Create media asset
      const newAsset = await MediaService.createMediaAsset(assetData);
      
      // Complete progress
      clearInterval(progressInterval);
      setProgress(100);
      
      setUploadSuccess(true);
      
      // Keep success message visible longer and don't auto-close
      // Let user manually close or the parent component handle closing
      if (onUploadComplete) {
        onUploadComplete(newAsset);
      }
      // Don't auto-clear the form - let user see the success message
      
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error.message || 'Error uploading image');
      clearInterval(progressInterval);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(`${img.width}x${img.height}`);
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const clearForm = () => {
    setSelectedFile(null);
    setCategory('');
    setTags('');
    setAltText('');
    setDescription('');
    setProgress(0);
    
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getRelevantTagSuggestions = () => {
    let relevant = [...tagSuggestions];
    
    if (category === 'story') {
      relevant = ['narrative', 'characters', 'scene', ...relevant];
    } else if (category === 'lesson') {
      relevant = ['educational', 'tutorial', 'learning', ...relevant];
    } else if (category === 'diagram') {
      relevant = ['educational', 'reference', 'chart', ...relevant];
    }
    
    return [...new Set(relevant)].slice(0, 8);
  };

  return (
    <div className="media-upload">
      <h3>Upload New Image</h3>
      
      {uploadError && (
        <div className="upload-error">
          <span>{uploadError}</span>
          <button 
            onClick={() => setUploadError('')}
            className="error-close-btn"
          >
            ×
          </button>
        </div>
      )}
      
      {uploadSuccess && (
        <div className="upload-success">
          <span>✅ Image uploaded successfully!</span>
        </div>
      )}

      <div className="upload-form">
        {/* File drop area */}
        <div 
          className={`file-drop-area ${preview ? 'has-preview' : ''}`}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={handleBrowseClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          
          {preview ? (
            <img src={preview} alt="Preview" className="image-preview" />
          ) : (
            <div className="drop-message">
              <div className="upload-icon">📷</div>
              <p>Drag & drop an image here or click to browse</p>
              <p className="file-types">Supports JPG, PNG, SVG, GIF, WebP</p>
            </div>
          )}
        </div>
        
        {selectedFile && (
          <div className="file-info">
            <span className="file-name">{selectedFile.name}</span>
            <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
          </div>
        )}

        {/* Form fields */}
        <div className="form-grid">
          <div className="form-group">
            <label>Category *</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading || !selectedFile}
            >
              <option value="">Select category</option>
              {categories.map(c => (
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Alt Text * (for accessibility)</label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Describe what's in the image"
              disabled={uploading || !selectedFile}
            />
          </div>

          <div className="form-group full-width">
            <label>Tags (comma-separated)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => handleTagInput(e.target.value)}
              placeholder="e.g. beginner, vocabulary, colorful"
              disabled={uploading || !selectedFile}
            />
            
            {/* Tag suggestions */}
            {selectedFile && (
              <div className="tag-suggestions">
                <small>Quick add: </small>
                {getRelevantTagSuggestions().map(tag => (
                  <button
                    key={tag}
                    type="button"
                    className="tag-suggestion-btn"
                    onClick={() => addSuggestedTag(tag)}
                    disabled={uploading}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group full-width">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description or notes about this image"
              rows="3"
              disabled={uploading || !selectedFile}
            />
          </div>
        </div>

        {/* Progress bar */}
        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="progress-text">
              {progress < 100 ? `Uploading... ${progress}%` : 'Processing...'}
            </div>
          </div>
        )}

        {/* Form actions */}
        <div className="form-actions">
          {uploadSuccess ? (
            <button
              onClick={clearForm}
              className="clear-button"
            >
              Upload Another Image
            </button>
          ) : (
            <>
              <button
                onClick={clearForm}
                disabled={uploading || !selectedFile}
                className="clear-button"
              >
                Clear
              </button>
              
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="upload-button"
              >
                {uploading ? 'Uploading...' : 'Upload Image'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaUpload;